import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui';

import { cn } from '../lib/utils';

// `transition-all` is there for hover feedback, which no touch device gives. On a coarse pointer
// the only thing it still does is smear the state change that a tap already decided over 150ms,
// which reads as the button lagging behind the finger, so drop it and let taps land instantly.
const buttonVariants = cva(
	"focus-visible:border-ring aria-invalid:ring-destructive/20 gap-2 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:aria-invalid:border-destructive/50 rounded-lg border border-transparent bg-clip-padding text-sm font-medium aria-invalid:ring-3 [&_svg:not([class*='size-'])]:size-4 inline-flex items-center justify-center whitespace-nowrap transition-all pointer-coarse:transition-none disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none shrink-0 [&_svg]:shrink-0 outline-none group/button select-none hover:cursor-pointer",
	{
		variants: {
			variant: {
				'default': 'bg-primary text-primary-foreground hover:bg-primary/80',
				'outline':
					'border-border bg-background hover:bg-muted hover:text-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50 aria-expanded:bg-muted aria-expanded:text-foreground',
				'outline-warm':
					'border-[#eed9c9] bg-[#fff7ed] hover:bg-[#ffedd5] hover:text-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50 aria-expanded:bg-muted aria-expanded:text-foreground',
				'secondary':
					'bg-secondary text-secondary-foreground hover:bg-secondary/80 aria-expanded:bg-secondary aria-expanded:text-secondary-foreground',
				'outline-dark': 'border-[#35433f] text-[#35433f] bg-transparent hover:bg-[#35433f]/5',
				'ghost':
					'hover:bg-muted hover:text-foreground dark:hover:bg-muted/50 aria-expanded:bg-muted aria-expanded:text-foreground',
				'destructive':
					'bg-destructive/10 hover:bg-destructive/20 dark:bg-destructive/20 text-destructive focus-visible:border-destructive/40 dark:hover:bg-destructive/30',
				'link': 'text-primary underline-offset-4 hover:underline',
				'outlinePrimary':
					'border-primary/30 text-primary bg-background hover:bg-primary/5 dark:bg-input/30 dark:border-primary/40 dark:hover:bg-primary/10',
				'dark': 'bg-foreground text-background hover:bg-foreground/80',
				'darkAlt': 'bg-foreground/90 text-background hover:bg-foreground/70 rounded-full!',
			},
			size: {
				'default': 'h-8 gap-1.5 px-2.5 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2',
				'xs': "h-6 gap-1 rounded-[min(var(--radius-md),10px)] px-2 text-xs in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3",
				'sm': "h-7 gap-1 rounded-[min(var(--radius-md),12px)] px-2.5 text-[0.8rem] in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3.5",
				'lg': 'h-9 gap-1.5 px-2.5 has-data-[icon=inline-end]:pr-3 has-data-[icon=inline-start]:pl-3',
				'xl': "h-12 rounded-[12px] text-base px-6 has-[>svg]:px-4 [&_svg:not([class*='size-'])]:size-6",
				'2xl': "h-14 rounded-[12px] text-base px-6 has-[>svg]:px-4 [&_svg:not([class*='size-'])]:size-7",
				'icon': 'size-8',
				'icon-xs':
					"size-6 rounded-[min(var(--radius-md),10px)] in-data-[slot=button-group]:rounded-lg [&_svg:not([class*='size-'])]:size-3",
				'icon-sm': 'size-7 rounded-[min(var(--radius-md),12px)] in-data-[slot=button-group]:rounded-lg',
				'icon-lg': 'size-9',
			},
		},
		defaultVariants: {
			variant: 'default',
			size: 'default',
		},
	},
);

function Button({
	className,
	variant = 'default',
	size = 'default',
	asChild = false,
	...props
}: React.ComponentProps<'button'> &
	VariantProps<typeof buttonVariants> & {
		asChild?: boolean;
	}) {
	const Comp = asChild ? Slot.Root : 'button';

	return (
		<Comp
			data-slot="button"
			data-variant={variant}
			data-size={size}
			className={cn(buttonVariants({ variant, size, className }))}
			{...props}
		/>
	);
}

export { Button, buttonVariants };
